import { useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { MiniTrend } from './MiniTrend';
import type { PidTuning } from '../../engine/control/pid';

const FIELDS: { key: keyof PidTuning; label: string; hint: string; step?: number }[] = [
  { key: 'kc', label: 'Kc', hint: 'Proportional gain, % output per % of PV span. Dimensionless.' },
  { key: 'ti', label: 'Ti (s)', hint: 'Integral time. Smaller is more aggressive; 0 disables integral action.' },
  { key: 'td', label: 'Td (s)', hint: 'Derivative time. 0 disables. Leave it at 0 on a noisy flow signal.' },
  { key: 'dFilterN', label: 'D filter N', hint: 'Derivative filter divisor; the filter time constant is Td/N. Typical 8–10.' },
  { key: 'opLo', label: 'OP low %', hint: 'Output clamp.' },
  { key: 'opHi', label: 'OP high %', hint: 'Output clamp.' },
  { key: 'opRateLimit', label: 'OP rate %/s', hint: 'Output slew limit. 0 = unlimited.' },
  { key: 'spRampRate', label: 'SP ramp /s', hint: 'Setpoint ramp rate in engineering units per second. 0 = step.' },
];

export function TuningTab({ loopId }: { loopId: string }): JSX.Element {
  const loop = useEngine((s) => s.snap?.loops.find((l) => l.id === loopId));
  const stepTest = useEngine((s) => s.snap?.stepTest);
  const send = useEngine((s) => s.send);
  const [bump, setBump] = useState(5);
  const [tauCFactor, setTauCFactor] = useState(1);
  const [drafts, setDrafts] = useState<Partial<Record<string, string>>>({});

  if (!loop) return <div style={{ padding: 10 }}>Loading…</div>;
  const t = loop.tuning;
  const testing = stepTest?.active && stepTest.loop === loopId;
  const fit = stepTest?.loop === loopId ? stepTest.fit : null;

  const commit = (key: keyof PidTuning, raw: string): void => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    send({ t: 'setTuning', loop: loopId, tuning: { [key]: n } as Partial<PidTuning> });
  };

  return (
    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 9, fontSize: 11.5 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 8px' }}>
        {FIELDS.map((f) => (
          <label key={f.key} title={f.hint} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="muted" style={{ flex: 1 }}>
              {f.label}
            </span>
            <input
              style={{ width: 62, textAlign: 'right' }}
              value={drafts[f.key] ?? String(t[f.key])}
              onChange={(e) => setDrafts({ ...drafts, [f.key]: e.target.value })}
              onBlur={(e) => {
                commit(f.key, e.target.value);
                setDrafts({ ...drafts, [f.key]: undefined });
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span className="muted">Action</span>
        <button
          className={t.direction === 'reverse' ? 'on' : ''}
          onClick={() => send({ t: 'setTuning', loop: loopId, tuning: { direction: 'reverse' } })}
        >
          Reverse
        </button>
        <button
          className={t.direction === 'direct' ? 'on' : ''}
          onClick={() => send({ t: 'setTuning', loop: loopId, tuning: { direction: 'direct' } })}
        >
          Direct
        </button>
        <button
          className={t.dOn === 'pv' ? 'on' : ''}
          title="Derivative on PV avoids a kick when the setpoint changes."
          onClick={() => send({ t: 'setTuning', loop: loopId, tuning: { dOn: t.dOn === 'pv' ? 'error' : 'pv' } })}
        >
          D on {t.dOn === 'pv' ? 'PV' : 'error'}
        </button>
      </div>

      <div style={{ borderTop: '1px solid var(--line-dim)', paddingTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <strong style={{ flex: 1 }}>Step test</strong>
          {!testing ? (
            <button
              disabled={loop.mode !== 'MAN'}
              title={loop.mode !== 'MAN' ? 'Put the loop in MAN first' : 'Start recording'}
              onClick={() => send({ t: 'stepTest', loop: loopId, op: 'start' })}
            >
              Start
            </button>
          ) : (
            <>
              <input
                style={{ width: 48, textAlign: 'right' }}
                value={bump}
                onChange={(e) => setBump(Number(e.target.value) || 0)}
              />
              <button onClick={() => send({ t: 'stepTest', loop: loopId, op: 'bump', deltaOp: bump })}>Bump %</button>
              <button onClick={() => send({ t: 'stepTest', loop: loopId, op: 'stop' })}>Fit</button>
            </>
          )}
        </div>

        {testing && (
          <div className="muted" style={{ fontSize: 10.5, marginBottom: 5 }}>
            Recording {stepTest?.samples ?? 0} samples over {((stepTest?.durationS ?? 0) / 60).toFixed(1)} min. Bump
            the output, wait for the PV to settle, then fit.
          </div>
        )}

        <MiniTrend
          windowS={1800}
          height={64}
          tags={[
            { tag: `${loopId}.PV`, colour: '#6fcf7d' },
            { tag: `${loopId}.OP`, colour: '#b9a7d6' },
          ]}
        />

        {fit && (
          <div style={{ marginTop: 7 }}>
            <div className={fit.ok ? 'muted' : ''} style={{ fontSize: 11, color: fit.ok ? undefined : 'var(--pri-medium)' }}>
              {fit.note}
            </div>
            {fit.ok && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '7px 0' }} title="Closed-loop time constant as a multiple of the dead time. Lower is tighter and less forgiving.">
                  <span className="muted">τc = {tauCFactor.toFixed(1)} × θ</span>
                  <input
                    type="range"
                    min={0.5}
                    max={3}
                    step={0.1}
                    value={tauCFactor}
                    onChange={(e) => setTauCFactor(Number(e.target.value))}
                    style={{ flex: 1 }}
                  />
                </label>
                <TuningSuggestions loopId={loopId} kp={fit.kp} tau={fit.tau} theta={fit.theta} tauC={tauCFactor * fit.theta} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TuningSuggestions({
  loopId,
  kp,
  tau,
  theta,
  tauC,
}: {
  loopId: string;
  kp: number;
  tau: number;
  theta: number;
  tauC: number;
}): JSX.Element {
  const send = useEngine((s) => s.send);
  // The rules are pure functions of the fit, so they are computed here rather than
  // round-tripped through the worker.
  const suggestions = suggest(kp, tau, theta, tauC);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {suggestions.map((s) => (
        <div
          key={s.method}
          style={{ border: '1px solid var(--line-dim)', borderRadius: 3, padding: '5px 7px', background: 'var(--bg-deep)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <strong style={{ fontSize: 11 }}>{s.method}</strong>
            <span className="num muted" style={{ fontSize: 10.5, flex: 1 }}>
              Kc {s.kc} · Ti {s.ti} · Td {s.td}
            </span>
            <button
              style={{ padding: '1px 7px', fontSize: 11 }}
              onClick={() => send({ t: 'setTuning', loop: loopId, tuning: { kc: s.kc, ti: s.ti, td: s.td } })}
            >
              Apply
            </button>
          </div>
          <div className="muted" style={{ fontSize: 10.2, marginTop: 3, lineHeight: 1.35 }}>
            {s.explanation}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Mirrors engine/control/steptest.ts; kept in sync by tests/engine/steptest.test.ts. */
function suggest(
  kp: number,
  tau: number,
  theta: number,
  tauC: number,
): { method: string; kc: number; ti: number; td: number; explanation: string }[] {
  const th = Math.max(theta, 1e-3);
  const tc = Math.max(tauC, 1e-3);
  const r = (v: number, d = 3): number => Math.round(v * 10 ** d) / 10 ** d;
  return [
    {
      method: 'SIMC (PI)',
      kc: r(tau / (kp * (tc + th))),
      ti: r(Math.min(tau, 4 * (tc + th)), 1),
      td: 0,
      explanation:
        'Skogestad’s rule: aims for a first-order closed loop of time constant τc. Tight but robust at τc = θ; slide τc up for a gentler response.',
    },
    {
      method: 'SIMC (PID)',
      kc: r(tau / (kp * (tc + th))),
      ti: r(Math.min(tau, 4 * (tc + th)), 1),
      td: r(th / 2, 1),
      explanation:
        'The same rule with derivative at half the dead time. Worth it on a clean, slow measurement; on a noisy flow it will chatter the valve.',
    },
    {
      method: 'Ziegler–Nichols (PI)',
      kc: r((0.9 * tau) / (kp * th)),
      ti: r(3.33 * th, 1),
      td: 0,
      explanation:
        'The 1942 reaction-curve rule, targeting quarter-amplitude decay. Expect ~25 % overshoot and a lively valve — a useful upper bound, rarely a final answer.',
    },
    {
      method: 'Cohen–Coon (PI)',
      kc: r((tau / (kp * th)) * (0.9 + th / (12 * tau))),
      ti: r((th * (30 + (3 * th) / tau)) / (9 + (20 * th) / tau), 1),
      td: 0,
      explanation:
        'Designed for dead-time-dominant processes (θ/τ > 0.3), where Ziegler–Nichols is too aggressive. Still quarter-decay, so still lively.',
    },
  ];
}
